import { AxiosError } from "axios";
import { getApi, putApi } from "./apis";

interface PurchaseRequest {
  id?: number;
  quantityPurchased: number;
  unitPrice: number;
  purchaseDate: string;
  invoiceRef: string;
  unit: string;
}

interface UpdateInventoryTransactionsRequest {
  productId: number;
  purchases: PurchaseRequest[];
}

const requestCache = new Map();

const cacheKey = (endpoint: string, params?: any) => {
  return `${endpoint}${params ? JSON.stringify(params) : ""}`;
};

const withCache = async (key: string, request: () => Promise<any>) => {
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  const promise = request();
  requestCache.set(key, promise);

  try {
    const result = await promise;
    setTimeout(() => requestCache.delete(key), 2000);
    return result;
  } catch (error) {
    requestCache.delete(key);
    throw error;
  }
};

// API Endpoints matching your backend exactly
export const fetchInventorySummaryApi = async () => {
  const endpoint = "InventoryManagement/summary";

  return withCache(cacheKey(endpoint), async () => {
    try {
      return await getApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.details || 
                            error.message;
        throw new Error(`Failed to fetch inventory summary: ${errorMessage}`);
      }
      if (error instanceof Error) {
        throw new Error(`Failed to fetch inventory summary: ${error.message}`);
      }
      throw new Error("Failed to fetch inventory summary: unknown error occurred");
    }
  });
};

export const fetchInventoryDetailApi = async (productId: number) => {
  const endpoint = `InventoryManagement/detail/${productId}`;

  return withCache(cacheKey(endpoint, productId), async () => {
    try {
      return await getApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.details || 
                            error.message;
        throw new Error(`Failed to fetch inventory detail: ${errorMessage}`);
      }
      if (error instanceof Error) {
        throw new Error(`Failed to fetch inventory detail: ${error.message}`);
      }
      throw new Error("Failed to fetch inventory detail: unknown error occurred");
    }
  });
};

export const updateInventoryTransactionsApi = async (data: UpdateInventoryTransactionsRequest) => {
  const endpoint = "InventoryManagement/transactions";

  try {
    const response = await putApi(endpoint, data);
    // Clear related cache entries after successful update
    requestCache.delete(cacheKey("InventoryManagement/summary"));
    requestCache.delete(cacheKey(`InventoryManagement/detail/${data.productId}`, data.productId));
    return response;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.details ||
                          error.response?.data?.error ||
                          error.message;
      throw new Error(errorMessage);
    }
    throw new Error("Failed to update inventory transactions");
  }
};

// Clear cache utility function
export const clearInventoryCache = () => {
  requestCache.clear();
};

// Clear specific product cache
export const clearProductCache = (productId: number) => {
  requestCache.delete(cacheKey("InventoryManagement/summary"));
  requestCache.delete(cacheKey(`InventoryManagement/detail/${productId}`, productId));
};